import { RefObject, useEffect, useRef, useState } from 'react';
import SVG from 'react-inlinesvg';
import { useSelector } from 'react-redux';
import { formatNumber } from 'modules/I18N';
import chevronRight from 'assets/icons/chevron_right.svg';
import info from 'assets/icons/info.svg';
import { InputSize } from 'components/common/types/Input.types';
import { Store } from 'components/common/types/Store.types';
import CollapsibleListStatsHeader from 'lib/CollapsibleList/CollapsibleListStatsHeader';
import CollapsibleCategoryButtons from 'lib/CollapsibleCategory/CollapsibleCategoryButtons';
import IncludeExcludeToggle from 'lib/IncludeExcludeToggle/IncludeExcludeToggle';
import InputNumber from 'lib/InputNumber';
import NavigablePanelLabel from 'components/common/NavigablePanelLabel';
import { POI_MAX_RADIUS, POI_MIN_RADIUS } from './consts';
import { POIOptionRowProps } from './PointsOfInterest.types';

const POIOptionRow: React.FC<POIOptionRowProps> = ({
  currentFocusedIndex,
  index,
  option,
  style,
  isDisabled,
  isMainLevel,
  isSelected,
  isIncluded,
  radius,
  includedChildrenNumber,
  excludedChildrenNumber,
  navigateForward,
  onIncludeExcludeChange,
  onChangeRadius,
  resetCurrentFocusedItemIndex,
  hideParentIncludeExclude,
  isCustomPOI,
  onDeleteCustomCategory,
  isDeselectAllowed = true,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { localeCode } = useSelector((state: Store) => state.publisher.configuration);
  const [isHovered, setIsHovered] = useState(false);

  const radiusInputRef: RefObject<HTMLInputElement> = useRef(null);
  const optionLabel = (isMainLevel ? option.categoryType : option.name) ?? '';
  const optionChildrenLength = (option.children ?? []).length;
  const haveChildren = optionChildrenLength > 0;
  const isIncludeExcludeHidden = haveChildren && hideParentIncludeExclude;

  const handleChangeRadius = (radiusValue: number): void => {
    onChangeRadius(radiusValue, option);
  };

  const radiusInfo = `Integer radius between ${POI_MIN_RADIUS} and ${formatNumber({ value: POI_MAX_RADIUS, localeCode })} m`;

  useEffect(() => {
    if (currentFocusedIndex === index) radiusInputRef.current?.focus({ preventScroll: true });
  }, [currentFocusedIndex]);

  return (
    <div
      style={style}
      className="flex flex-col justify-center items-center border-b border-neutral-950-opacity-10 last:border-b-0"
      data-test-id={`poi-row-${index}`}
      onMouseEnter={() => isCustomPOI && setIsHovered(true)}
      onMouseLeave={() => isCustomPOI && setIsHovered(false)}
    >
      <div
        key={isMainLevel ? option.categoryType : option.code}
        className="w-full py-1 flex flex-row-reverse justify-between items-center"
      >
        <div className="flex flex-row-reverse items-center">
          <div className="w-4 h-4">
            {haveChildren && (
              <button
                type="button"
                data-test-id={`arrow-button-${index}`}
                onClick={() => {
                  if (haveChildren && option.name && option.children) {
                    navigateForward(option.name, option.categoryPath);
                    resetCurrentFocusedItemIndex();
                  }
                }}
              >
                {haveChildren && <SVG src={chevronRight} className="fill-current text-neutral-900 w-4 h-4" />}
              </button>
            )}
          </div>

          {!isDisabled && !isIncludeExcludeHidden && (
            <IncludeExcludeToggle
              option={{ ...option, code: option.name, include: isIncluded }}
              onIncludeExcludeChange={onIncludeExcludeChange}
              isAlwaysVisible
              isSelected={isSelected}
              isOptionNameSelectable={false}
              isHovered={false}
              isDeselectAllowed={isDeselectAllowed}
            />
          )}
        </div>
        <div className="flex items-center min-w-0">
          <NavigablePanelLabel
            label={optionLabel}
            isNavigable={haveChildren && !!option.name && !!option.children}
            onNavigate={() => {
              navigateForward(option.name, option.categoryPath);
              resetCurrentFocusedItemIndex();
            }}
            isDisabled={isDisabled}
          />
          {isCustomPOI && isHovered && onDeleteCustomCategory && (
            <div className="ml-1 flex-shrink-0" data-test-id={`custom-poi-delete-${index}`}>
              <CollapsibleCategoryButtons onRemove={onDeleteCustomCategory} />
            </div>
          )}
          <span className="align-bottom">
            {haveChildren && (
              <CollapsibleListStatsHeader
                available={optionChildrenLength}
                included={includedChildrenNumber}
                excluded={excludedChildrenNumber}
                isDisabled={isDisabled}
              />
            )}
          </span>
        </div>
      </div>
      {isSelected && !isDisabled && radius !== undefined && (
        <div className="flex w-full pt-2 pr-3">
          <div className="flex-shrink-0 mr-8 text-essential-tertiary sub-header-sm">Add radius</div>
          <div className="w-full">
            <InputNumber
              dataTestId={`radius-input-${index}`}
              inputRef={radiusInputRef}
              key={option.name || option.categoryType}
              min={POI_MIN_RADIUS}
              max={POI_MAX_RADIUS}
              maximumFractionDigits={0}
              onChange={handleChangeRadius}
              size={InputSize.SMALL}
              value={radius}
              name={option.name || option.categoryType}
            />
            <div className="flex items-center gap-1 mt-1">
              <SVG src={info} className="text-secondary h-4 w-4" />
              <div className="body-sm text-essential-tertiary">{radiusInfo}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POIOptionRow;
